// Source-of-truth for kernel render primitive schemas.
//
// Pure JS so it can be imported from the LLM tool (kernelRenderTool),
// validators (kernelStore, kernelTestTool), and the React renderer
// (components/kernelPrimitives) without dragging the React tree into
// the analytics path. The component files in components/kernelPrimitives
// must match the schemas declared here — anything you add as a `kind`
// below has to ship with a matching component + a `case` registered
// in components/kernelPrimitives/index.jsx, and vice versa.

// Per-kind documentation that gets embedded into the render_block
// tool description. Each entry is a paragraph the LLM reads to pick
// the right shape. Keep them tight and example-shaped — they're tokens
// in every chat message the model sends.
export const PRIMITIVE_SPEC_DOCS = {
    brief: `
brief:
  {kind: "brief", format?: ..., title?: string, evidence?: "bar"|"none",
   maxItems?: number, empty?: string}
  Decision-ready findings stated in words, with optional impact bars as evidence.
  Findings must arrive pre-ranked; this renderer never invents priority.
  data: {summary?: {label?, value?, format?, detail?},
         findings: [{id?, severity?: "critical"|"warning"|"positive"|"neutral",
                     headline: string, detail?, impact?, format?, focus?: {field, value}}],
         disclosure?: string|{text}}
`.trim(),

    metricRail: `
metricRail:
  {kind: "metricRail", title?: string, format?: ..., maxItems?: number, empty?: string}
  A stable vertical instrument panel. Each row prints one current value and may
  compare it with a reference using a bullet bar. favorable is "up", "down", or
  "neutral" and controls only tone, never the value; omitted is neutral. An
  explicit delta is a fractional ratio (0.005 means 0.5%).
  data: [{id?, label, value, format?, reference?, referenceLabel?, delta?,
          favorable?: "up"|"down"|"neutral", tone?, note?}]
`.trim(),

    distribution: `
distribution:
  {kind: "distribution", labelField?: string, valueField?: string,
   weightField?: string, format?: ..., maxGroups?: number, title?, empty?}
  Compact box-and-whisker rows on one shared linear scale. Use when the shape and
  spread of a numeric measure matter; each row prints its median.
  data: [{<labelField>: string, <valueField>: number, <weightField>?: number}]
`.trim(),

    shareBar: `
shareBar:
  {kind: "shareBar", format?: ..., title?: string, height?: number,
   maxItems?: number, empty?: string}
  One 100% composition bar with value-bearing category controls. Pass either one
  segment list or tab objects. Clicking hides a category and renormalizes; when a
  tab declares field, shift-click emits the segment key as a canvas focus.
  data: [{key?, label, value|count, color?}]
        OR [{id, label?, field?, lede?, segments: [{key?, label, value|count, color?}]}]
`.trim(),

    waterfall: `
waterfall:
  {kind: "waterfall", format?: ..., favorable?: "up"|"down"|"neutral",
   groupField?, focusValue?, maxItems?, title?, height?, empty?}
  A start value, signed contributions, and an end value. The renderer checks that
  the contributions reconcile and reports drift rather than hiding it. Omitted
  favorable is neutral.
  data: {start: {label?, value}, end: {label?, value}, steps: [{label, delta}]}
`.trim(),

    deltaBars: `
deltaBars:
  {kind: "deltaBars", format?: ..., favorable?: "up"|"down"|"neutral",
   groupField?, focusValue?, maxItems?, empty?}
  Signed categorical changes on one shared zero line. Use when individual movers
  matter more than the running-total geometry of a waterfall. Omitted favorable
  is neutral.
  data: {start?: {label?, value}, end?: {label?, value}, steps: [{label, delta}]}
        OR [{label, value|delta}]
`.trim(),

    refBars: `
refBars:
  {kind: "refBars", format?: ..., reference?: {value, label?}, groupField?,
   focusValue?, maxItems?, empty?}
  Ranked bars with one shared reference rule. Use for threshold and benchmark
  comparisons; rows stay on one proportional scale.
  data: [{label, value, detail?}]
`.trim(),

    slopegraph: `
slopegraph:
  {kind: "slopegraph", labelField?: string, leftField?: string,
   rightField?: string, leftLabel?, rightLabel?, format?: ...,
   favorable?: "up"|"down"|"neutral", groupField?, focusValue?,
   maxItems?, title?, height?, empty?}
  Two-period comparison: position is level, slope is change, and crossings are
  rank changes. Values are printed at both ends. Omitted favorable is neutral.
  data: [{<labelField>: string, <leftField>: number, <rightField>: number}]
`.trim(),

    sparkline: `
sparkline:
  {kind: "sparkline", series?: string, color?: string, height?: number, label?: string, empty?: string}
  data: number[] OR [{<series>: number, ...}, ...]
`.trim(),

    chart: `
chart:
  {kind: "chart", mode?: "line"|"stacked",
   series?: number[],                          // line mode, inline values
   valueField?: string,                        // line mode, when data is row[]
   layers?: [{label, series: number[], color?}],   // stacked, inline
   layerFields?: [{label, field, color?}],     // stacked, when data is row[]
   range?: "1D"|"5D"|"1M"|"1Q",            // controls hover-tooltip bucket label
   height?: number, title?: string, seriesLabel?: string}
  data (overrides any inline series/layers in spec):
    number[]                                   // → fills series, mode = "line"
    OR {series: number[]}                      // → fills series
    OR {layers: [{label, series, color?}]}     // → fills layers, mode = "stacked"
    OR [{<valueField>: number, ...}, ...]      // row[] (line) — auto-projects via valueField
    OR [{<field>: number, ...}, ...]           // row[] (stacked) when layerFields is set
  CHOOSING mode — this is a correctness decision, not a style one:
    • "line"   — series share the Y-axis as INDEPENDENT overlays. Use this for any
                 rolling-average overlay, a comparison line on top of a count, a
                 reference line, or anywhere you want the eye to read each series at
                 its own true value. Multiple layers are drawn as separate lines, not
                 summed.
    • "stacked" — layers ADD UP. The visible height at each x is the SUM of all
                 layers at that x. Only correct when the layers are parts of a whole
                 (subcategory volumes that sum to the total). NEVER use stacked for a
                 rolling average + raw count, two metrics being compared, or any
                 series that aren't additive — the chart will be visually wrong even
                 if the numbers underneath are right.
    If in doubt, choose "line".
  IMPORTANT for kernel-friendly charts: when your data is rows from an analytics_query,
  pass the rows as the "data" argument and set "valueField" (line) or "layerFields" (stacked)
  on the spec instead of inlining numbers into "series". The kernel save/re-run path replays
  the spec against fresh query results — inline numbers freeze; field references re-project.
  Use this for time-series surfaces (counts per day, totals over time, stacked layers by
  category). For single buckets / count comparisons, prefer the "bars" kind.
`.trim(),

    stat: `
stat:
  {kind: "stat", label: string, valueField?: string, deltaField?: string,
   format?: "number"|"integer"|"percent"|"0.00"|"0.0"|"duration_ms"|"duration_s"|"timestamp"|"date",
   deltaFormat?: same set,
   tone?: "neutral"|"good"|"bad"|"muted",
   sublabel?: string}
  data: number OR {value, delta, ...}
`.trim(),

    bars: `
bars:
  {kind: "bars", labelField: string, valueField: string, format?: ..., maxItems?: number,
   color?: string, link?: {dim, idField, labelField?}, empty?: string}
  data: [{<labelField>: string, <valueField>: number, ...}, ...]
`.trim(),

    table: `
table:
  {kind: "table", detailField?: string, detailColumns?: [column, ...],
   merge?: {by: string, fields: {<field>: "max"|"min"|"sum"}},
   detailQuery?: {spec: analytics_query_spec_with_{$row:"column"}_value_markers}, columns: [
    {id, label?, format?, align?: "left"|"right", tone?, dim?, weight?: number, emptyText?: string, unresolvedText?: string,
     link?: {dim, idField?, labelField?},
     spark?: {field?: string, color?: string}}, ...],
   maxRows?: number, empty?: string}
  data: [{<column.id>: value, ...}, ...]
  "weight" gives a column a flex-grow multiplier (default 1) so a content column
  can claim more of the free width; "emptyText" renders in place of a blank cell
  when the row has no value. format "humanize" turns a machine key such as
  "api_security_review" into "API security review" for display only.
  "unresolvedText" replaces a linked id when no display label resolves. "merge"
  collapses rows sharing a display key after upstream label normalization; name
  every aggregate explicitly so counts are never assumed additive.
  A column with "spark" renders an inline mini line-chart from a per-row ARRAY of
      With "detailField", a row whose value at that key is a non-empty array
      renders as expandable: clicking (or Enter/Space) discloses those rows
      rendered with "detailColumns". Opt-in - a table without it is unchanged,
      and a row carrying no detail rows stays inert.
      With "detailQuery", opening a row runs that analytics query after replacing
      each {$row:"column"} value marker from the parent row. Only one lazy detail
      request is active per table; use a hard result limit in the query.
  numbers (read from row[spark.field], else row[column.id]) — e.g. a per-entity
  daily trend sitting next to its label + total, with the label still clickable
  via link. Wide tables scroll horizontally and every table has a CSV download,
  so for "top N entities over time" prefer ONE table with a label column, a total,
  and a spark column over dozens of per-bucket numeric columns.
`.trim(),

    rows: `
rows:
  {kind: "rows", primaryField: string, secondaryField?: string,
   rightField?: string, rightFormat?: ..., rightTone?: ...,
   maxRows?: number, wrap?: boolean, link?: {dim, idField, labelField?}, empty?: string}
  wrap: true keeps the whole primary text on screen over several lines
  instead of truncating it to one; use it when rows are sentences.
  data: [{<primaryField>: string, <secondaryField>?: string, <rightField>?: any, ...}, ...]
`.trim(),

    // ── new primitives ────────────────────────────────────────────

    kpis: `
kpis:
  {kind: "kpis", tiles: [
    {label, valueField?, deltaField?, format?, deltaFormat?, tone?, sublabel?}, ...]}
  Multi-tile KPI row. Pass an array of objects via data — the tiles index in lockstep
  with data[i] (so tiles[0] reads data[0][tiles[0].valueField]), OR pass a single object
  and let every tile pull its own valueField from it. Use 2-5 tiles for a top-of-surface
  summary. Prefer "stat" for a single number.
  data: {field1: ..., field2: ...} OR [{value, delta, ...}, ...]
`.trim(),

    donut: `
donut:
  {kind: "donut", valueField?: string, maxField?: string, max?: number,
   label?: string, sublabel?: string, format?: ..., color?: string, height?: number}
  Single ratio rendered as a donut with the percentage in the middle. Defaults max=1
  (treats value as 0-1). When max>1 the ratio is value/max. Use for cache-hit-rate,
  success-rate, settled-rate — anywhere you want a single 0-100 reading to read at a
  glance. Prefer "stat" if the absolute number matters more than the ratio.
  data: number OR {value, max?}
`.trim(),

    heatmap: `
heatmap:
  {kind: "heatmap", xField: string, yField: string, valueField: string,
   xLabels?: string[], yLabels?: string[], format?: ..., color?: string,
   title?: string, empty?: string}
  2D dot-matrix grid (rows × columns → value), one dot per cell whose SIZE and
  BRIGHTNESS both scale with the value (√-scaled against the peak); empty cells
  show a faint placeholder so gaps read. Use when you have two categorical
  dimensions (provider × hour-of-day, model × day-of-week) and one value. If your
  x dimension is time and rows are categories, use this over a stacked chart —
  the per-cell magnitude is the insight.
  Fit: heatmaps need MANY rows to be worth it (~8+ categories). For a FEW categories
  over time (e.g. 3-5 skills across 30 days), a heatmap wastes the grid — prefer a
  table with a "spark" column (label + total + inline trend, label still clickable),
  which is denser and more legible for small category counts. Do NOT set a large
  height for few rows; row height is derived from the row count.
  data: [{<xField>: any, <yField>: any, <valueField>: number}, ...]
`.trim(),

    calendar: `
calendar:
  {kind: "calendar", dateField: string, valueField: string, weeks?: number,
   color?: string, format?: ..., title?: string, empty?: string}
  GitHub-style calendar grid: 7 rows (days of week) × N columns (weeks back from
  today). Default weeks=26. Use for "activity per day over a long window" where the
  weekly pattern matters as much as the trend.
  data: [{<dateField>: string|number (date), <valueField>: number}, ...]
`.trim(),

    scatter: `
scatter:
  {kind: "scatter", xField: string, yField: string, seriesField?: string,
   xLabel?: string, yLabel?: string, xFormat?: ..., yFormat?: ...,
   sizeField?: string, color?: string, title?: string, height?: number, empty?: string}
  Two-numeric correlation. Each row is one point at (x, y). Optional seriesField
  splits into colored groups; optional sizeField scales the dot radius. Use when
  you need to read the *relationship* between two metrics (latency × tokens,
  cost × cache-hit). If x is time, use "chart" instead.
  data: [{<xField>: number, <yField>: number, <seriesField>?: string, <sizeField>?: number}, ...]
`.trim(),

    funnel: `
funnel:
  {kind: "funnel", labelField: string, valueField: string,
   color?: string, format?: ..., empty?: string}
  Ordered drop-off across pipeline stages. Each row is one stage; bars shrink in
  data order. Annotates each stage with its share of the FIRST stage (i.e. running
  retention). Use for prompt → tool-call → settled-style sequences. If stages are
  unordered counts, use "bars".
  data: [{<labelField>: string, <valueField>: number}, ...]  (in stage order)
`.trim(),

    boxplot: `
boxplot:
  {kind: "boxplot", labelField: string,
   p50Field: string, p95Field: string, p99Field: string, minField?: string, maxField?: string,
   format?: ..., color?: string, empty?: string}
  Quantile distributions per row. Each row gets a horizontal box (p50 = center line,
  p95 = box edge, p99/max = whisker end). Use for latency-by-model, duration-by-
  endpoint — anything where the *shape* of the distribution matters, not just the
  mean.
  data: [{<labelField>: string, <p50Field>: number, <p95Field>: number, <p99Field>: number, ...}, ...]
`.trim(),

    treemap: `
treemap:
  {kind: "treemap", labelField: string, valueField: string,
   color?: string, format?: ..., maxItems?: number, empty?: string, title?: string, height?: number}
  Squarified treemap of proportional values. Use for composition where the relative
  sizes are the main insight (tokens-by-model, cost-by-endpoint). Prefer "bars" if
  you need precise comparisons; treemap is for "which dominates."
  data: [{<labelField>: string, <valueField>: number}, ...]
`.trim(),

    composition: `
composition:
  {kind: "composition", labelField: string, valueField: string, splitField?: string,
   splitOrder?: string[], sharedScale?: boolean, splitColors?: {<splitValue>: "#rrggbb"},
   valueLabel?: string, maxItems?: number, empty?: string}
  splitField renders one titled bar per distinct value of that column, each
  ranking, coloring and totalling independently, so a segment's percentage is
  its share of its OWN category. splitOrder pins the leading categories; the
  rest follow by descending total. Anything past maxItems folds into one
  neutral "Other" segment per bar. sharedScale makes each split bar's width
  proportional to the largest group total instead of normalizing every group
  to full width. splitColors assigns one canvas-safe literal color per group;
  use it when group color has a documented semantic meaning. valueLabel names
  the additive unit shown beside each group total.
  100%-stacked horizontal composition bar with a toggleable chip legend — the
  Inventory Surface's fleet-composition look. Segments are ranked by value and
  share one full-width bar, so relative share reads at a glance. Use for a
  distribution across categories where the parts sum to a meaningful whole
  (endpoints per provider, installs per agent). Prefer "bars" when precise
  per-item comparison matters more than share-of-total.
  data: [{<labelField>: string, <valueField>: number}, ...]
`.trim(),

    timeline: `
timeline:
  {kind: "timeline", timeField: string, titleField: string,
   bodyField?: string, toneField?: string, format?: "timestamp"|"date",
   maxItems?: number, empty?: string,
   link?: {dim, idField, labelField?}}
  Vertical chronological event list with a connecting rail. Use for activity feeds,
  incident sequences, deploy history. Prefer "rows" for ranked/top-N data without
  a time axis.
  data: [{<timeField>: string|number, <titleField>: string, <bodyField>?: string, <toneField>?: "good"|"bad"|"neutral"|"muted"}, ...]
`.trim(),

    radar: `
radar:
  {kind: "radar", axes: [{label, field, max?: number}, ...],
   seriesField?: string, color?: string, height?: number, empty?: string}
  Multi-dimensional comparison across 3-6 axes. Each row is one polygon; if a
  seriesField is set, rows are grouped by series name. Per-axis max defaults to
  the data's max for that axis. Use for "strengths across dimensions" — throughput
  vs latency vs cost vs cache-hit. Read shape, not absolute values.
  data: [{<axes[i].field>: number, <seriesField>?: string}, ...]
`.trim(),

    parallel: `
parallel:
  {kind: "parallel", axes: [{label, field, format?: ..., reverse?: boolean}, ...],
   seriesField?: string, color?: string, height?: number, empty?: string}
  N-dimensional tradeoff comparison. Each row is one polyline crossing N vertical
  axes; each axis is independently scaled. seriesField (optional) colors the lines
  into groups. axes[i].reverse=true flips the axis so "good" is on top. Use for
  "no model dominates on every dimension" — throughput / latency / cost / cache-hit.
  data: [{<axes[i].field>: number, <seriesField>?: string}, ...]
`.trim(),

    sankey: `
sankey:
  {kind: "sankey", sourceField: string, targetField: string, valueField: string,
   nodeColor?: string, height?: number, empty?: string}
  Weighted flows between source and target nodes. Each row is one flow; node
  positions and link curves are auto-laid-out. Use for "X → Y → Z" routing or
  cohort movement. Edges must be acyclic (otherwise layers can't be resolved).
  data: [{<sourceField>: string, <targetField>: string, <valueField>: number}, ...]
`.trim(),

    chord: `
chord:
  {kind: "chord", sourceField: string, targetField: string, valueField: string,
   color?: string, height?: number, empty?: string}
  Circular many-to-many flow matrix. Each row is one directed weight from source
  to target; nodes sit on a ring and ribbons connect them. Use when you need to
  see ALL pairwise flows at once (endpoint × model usage) — sankey is better for
  staged left-to-right pipelines.
  data: [{<sourceField>: string, <targetField>: string, <valueField>: number}, ...]
`.trim(),

    sunburst: `
sunburst:
  {kind: "sunburst", pathField: string, valueField: string,
   separator?: string, color?: string, height?: number, empty?: string}
  Radial hierarchy via concentric arcs. Each row's path is split by "separator"
  (default "/") into hierarchy levels; arcs at each level size by summed value
  below. Use for hierarchical composition (provider/model, region/cluster/host).
  data: [{<pathField>: string ("level1/level2/..."), <valueField>: number}, ...]
`.trim(),

    relations: `
relations:
  {kind: "relations", title?: string, groups?: [string, ...], groupTotals?: {group: number},
   maxPerGroup?: number, caption?: string, empty?: string}
  data: {nodes: [{id, group, label?, note?, highlight?: boolean}, ...],
         links: [{source: id, target: id, kind?: string}, ...]}
  Entities of several kinds and the relations among them, drawn as one
  column per group (in groups order, else first seen) with a dot per entity
  and a line per link. Highlighted entities are filled and sorted first;
  each column shows at most maxPerGroup (default 10) and says how many more
  it holds (groupTotals overrides the count when the data is a sample).
  Use it for "which sessions ran on which endpoints" style questions where
  the answer is a set of things and how they connect, not a quantity. For
  weighted flows prefer sankey; for a single relation matrix prefer chord.
`.trim(),

    markdown: `
markdown:
  {kind: "markdown", template: string, title?: string, empty?: string}
  Narrative / textual answer rendered from a deterministic template (NOT an
  LLM) projected over the renderSource step's data, then displayed as markdown.
  This is the "the answer is prose, not a chart" primitive — a written summary,
  a labelled readout, a "top-N with commentary" list. Reach for it when you'd
  otherwise TYPE the answer in the chat: wrapping it as a markdown kernel makes
  that prose re-runnable against fresh data. Prefer a chart/table kind when the
  data shape itself is the insight.
  Template syntax (a small Handlebars subset — interpolation runs against the
  render DATA, never kernel params):
    {{ field }}                  interpolate a field of the current row/object
    {{ field | number }}         ...through a formatter (number, integer, percent,
                                 0.00, 0.0, duration_ms, duration_s, timestamp, date)
    {{#each rows}} … {{/each}}   loop the row list; inside, {{field}} is the row's
                                 field, {{@index}} is 0-based, {{this}} is the item;
                                 an optional {{else}} arm renders when the list is empty
    {{#if field}} … {{else}} … {{/if}}   branch on truthiness (empty list ⇒ false)
  The renderSource rows are exposed as \`rows\`; a single-object data also exposes
  its own keys at the top level. Markdown is the output, so use #, -, **bold**,
  tables, etc. freely.
  data: [{...}, ...]  (the renderSource rows) OR a single object
`.trim(),
};

// Ordered kind list — drives both KNOWN_RENDER_KINDS and the assembled
// SPEC_DOC. Ordering controls how the LLM scans options in the tool
// description; group cousins together so the model picks well.
export const PRIMITIVE_KINDS = [
    // narrative / standing state
    "brief",
    "metricRail",
    // single-value
    "stat",
    "kpis",
    "donut",
    "sparkline",
    // time / series
    "chart",
    // categorical
    "bars",
    "rows",
    "table",
    "deltaBars",
    "refBars",
    // composition
    "treemap",
    "sunburst",
    "composition",
    "shareBar",
    // shape / distribution
    "boxplot",
    "distribution",
    "scatter",
    "slopegraph",
    "waterfall",
    "radar",
    "parallel",
    // density / activity
    "heatmap",
    "calendar",
    "timeline",
    // flow
    "funnel",
    "sankey",
    "chord",
    "relations",
    // narrative
    "markdown",
];

export const KNOWN_RENDER_KINDS = new Set(PRIMITIVE_KINDS);

export const SPEC_DOC = `
spec shapes by kind (pass JSON-serialisable values only):

${PRIMITIVE_KINDS.map((k) => PRIMITIVE_SPEC_DOCS[k]).filter(Boolean).join("\n\n")}
`.trim();
